import { Fragment, useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Badge from "@mui/material/Badge";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import MaterialSymbol from "@/components/MaterialSymbol";
import { api } from "@/api/client";
import { useEventStream } from "@/hooks/useEventStream";
import { formatDateWith, getCachedDateFormat } from "@/hooks/useDateFormat";
import type { Announcement } from "@/types";

// Once-per-session guard for the login pop-up.
const POPUP_SESSION_KEY = "whatsNewPopupShown";

const CATEGORY_COLORS: Record<string, string> = {
  feature: "#2889ff",
  fix: "#33cc58",
  maintenance: "#ffa31f",
  security: "#c7527d",
  announcement: "#774fcc",
};

function categoryColor(cat: string): string {
  return CATEGORY_COLORS[cat] ?? "#6b7280";
}

export default function WhatsNewBell() {
  const { t } = useTranslation(["notifications", "common"]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [items, setItems] = useState<Announcement[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailItem, setDetailItem] = useState<Announcement | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<Announcement[]>("/announcements");
      const list = Array.isArray(res) ? res : [];
      setItems(list);
      return list;
    } catch {
      return [] as Announcement[];
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await api.get<{ count: number }>("/announcements/unread-count");
      const n = typeof res?.count === "number" ? res.count : 0;
      setUnreadCount(n);
      return n;
    } catch {
      return 0;
    }
  }, []);

  const markAllSeen = useCallback(async () => {
    try {
      await api.post("/announcements/mark-all-seen");
      setUnreadCount(0);
      setItems((prev) => prev.map((a) => ({ ...a, is_read: true })));
    } catch {
      // ignore
    }
  }, []);

  // Initial load: badge count + one-time login pop-up when there's unread news.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const count = await fetchUnreadCount();
      if (cancelled || count <= 0) return;
      if (sessionStorage.getItem(POPUP_SESSION_KEY)) return;
      sessionStorage.setItem(POPUP_SESSION_KEY, "1");
      await fetchList();
      if (!cancelled) setDialogOpen(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchUnreadCount, fetchList]);

  // Live-update the badge when an announcement is published.
  useEventStream(
    useCallback(
      (event: Record<string, unknown>) => {
        if (event.event === "announcement.published") {
          fetchUnreadCount();
        }
      },
      [fetchUnreadCount]
    )
  );

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    fetchList();
    if (unreadCount > 0) markAllSeen();
  };
  const handleClose = () => setAnchorEl(null);

  const handleDialogClose = () => {
    setDialogOpen(false);
    if (unreadCount > 0) markAllSeen();
  };

  const open = Boolean(anchorEl);
  const dateFmt = getCachedDateFormat();

  const renderItem = (a: Announcement, full: boolean, onClick?: () => void) => (
    <ListItem
      key={a.id}
      alignItems="flex-start"
      onClick={onClick}
      sx={{
        px: full ? 0 : 2,
        py: 1.25,
        ...(onClick
          ? { cursor: "pointer", "&:hover": { bgcolor: "action.hover" } }
          : {}),
      }}
    >
      <ListItemText
        disableTypography
        primary={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25 }}>
            <Chip
              label={t(`whatsNew.cat.${a.category}`, { defaultValue: a.category })}
              size="small"
              sx={{
                height: 18,
                fontSize: "0.65rem",
                fontWeight: 700,
                color: "#fff",
                bgcolor: categoryColor(a.category),
              }}
            />
            <Typography variant="body2" sx={{ fontWeight: 700, flex: 1 }}>
              {a.title}
            </Typography>
          </Box>
        }
        secondary={
          <>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={
                full
                  ? { whiteSpace: "pre-wrap", lineHeight: 1.5, mb: 0.75 }
                  : {
                      // Clean 2-line clamp with an ellipsis instead of a hard
                      // mid-line cut. Collapse newlines so the preview reads as
                      // one flowing summary.
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      lineHeight: 1.45,
                      mb: 0.5,
                    }
              }
            >
              {full ? a.body : a.body.replace(/\s+/g, " ").trim()}
            </Typography>
            {a.published_at && (
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ display: "block", fontSize: "0.7rem" }}
              >
                {formatDateWith(dateFmt, a.published_at)}
              </Typography>
            )}
          </>
        }
      />
    </ListItem>
  );

  return (
    <>
      <Tooltip title={t("whatsNew.title")}>
        <IconButton sx={{ color: "#fff", ml: 0.5 }} onClick={handleOpen}>
          <Badge badgeContent={unreadCount} color="error" max={99} invisible={unreadCount === 0}>
            <MaterialSymbol icon="campaign" size={24} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { width: 420, maxHeight: 540 } } }}
      >
        <Box sx={{ display: "flex", alignItems: "center", px: 2, py: 1.5 }}>
          <Typography sx={{ fontWeight: 700, flex: 1 }}>{t("whatsNew.title")}</Typography>
        </Box>
        <Divider />
        {loading && items.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <MaterialSymbol icon="celebration" size={32} color="#999" />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t("whatsNew.empty")}
            </Typography>
          </Box>
        ) : (
          <List dense sx={{ maxHeight: 460, overflow: "auto", py: 0 }}>
            {items.map((a, i) => (
              <Fragment key={a.id}>
                {i > 0 && <Divider component="li" />}
                {renderItem(a, false, () => {
                  setAnchorEl(null);
                  setDetailItem(a);
                })}
              </Fragment>
            ))}
          </List>
        )}
      </Popover>

      {/* One-time login pop-up */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <MaterialSymbol icon="campaign" size={24} />
          {t("whatsNew.dialogTitle")}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t("whatsNew.dialogSubtitle")}
          </Typography>
          <List sx={{ py: 0 }}>
            {items
              .filter((a) => !a.is_read)
              .map((a, i) => (
                <Fragment key={a.id}>
                  {i > 0 && <Divider component="li" sx={{ my: 0.5 }} />}
                  {renderItem(a, true)}
                </Fragment>
              ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} variant="contained">
            {t("whatsNew.gotIt")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Single-announcement detail (click-through from the panel) */}
      <Dialog
        open={!!detailItem}
        onClose={() => setDetailItem(null)}
        maxWidth="sm"
        fullWidth
        disableRestoreFocus
      >
        {detailItem && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                <Chip
                  label={t(`whatsNew.cat.${detailItem.category}`, {
                    defaultValue: detailItem.category,
                  })}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    color: "#fff",
                    bgcolor: categoryColor(detailItem.category),
                  }}
                />
                {detailItem.published_at && (
                  <Typography variant="caption" color="text.disabled">
                    {formatDateWith(dateFmt, detailItem.published_at)}
                  </Typography>
                )}
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                {detailItem.title}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                {detailItem.body}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailItem(null)}>{t("common:actions.close")}</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
}
